import type { MessageDescriptor } from '@lingui/core';
import type { z } from 'zod';

import type { StepUpAction, StepUpGrant } from '@/shared/http';

export type ActionKind = 'read' | 'navigate' | 'draft' | 'write' | 'destructive';

/**
 * Texto que o agente lê (U46): `msg` em pt-BR, servido no idioma do usuário. `string` é dívida da U33
 * antiga (inglês canônico), aceita só na transição e listada em `STRING_DESCRIPTION_DEBT`.
 */
export type AgentText = MessageDescriptor | string;

export interface Permission {
  readonly resource: string;
  readonly action: string;
  readonly strict?: boolean;
}

/**
 * "Qualquer uma de N": o `permission: [...]` do handle legado (`RoutePermissionGuard.tsx:30-31`).
 * Cada item leva o próprio `strict`; lista vazia nega, como `[].some()`.
 */
export interface AnyOfPermission {
  readonly anyOf: readonly Permission[];
}

/** Uma permissão ou "qualquer uma de N". */
export type PermissionRule = Permission | AnyOfPermission;

export interface Navigation {
  to(
    path: string,
    options?: { search?: Record<string, unknown>; replace?: boolean },
  ): Promise<void> | void;
  search(patch: Record<string, unknown>): Promise<void> | void;
}

export interface ActionContext<Input> {
  readonly input: Input;
  readonly nav: Navigation;
  /** A autorização de uso único da ação com `stepUp: { action }` (step-up novo); vai como `{ stepUp }` na chamada. */
  readonly stepUp?: StepUpGrant;
}

/**
 * Step-up novo da CreditAPI (`X-UY3-StepUp`): a ação do enum e o registro da rota protegida. O executor da tela
 * pede senha + código no lugar da confirmação e só então roda o `run`, com a autorização em `context.stepUp`.
 */
export interface ScreenStepUpGrant<Input> {
  readonly action: StepUpAction;
  readonly resourceId?: (input: Input) => string | null | undefined;
}

export interface ActionPreview {
  readonly title: string;
  readonly detail?: string;
  readonly tone?: 'default' | 'destructive';
}

/** Como o chat do Luy chama a ação rodando (`running`), terminada (`done`) e terminada sem achar nada (`empty`). */
export interface ActionActivity {
  readonly running: MessageDescriptor;
  readonly done: MessageDescriptor;
  readonly empty?: MessageDescriptor;
}

interface ActionBase<Input> {
  readonly id: string;
  readonly label: MessageDescriptor;
  readonly description: AgentText;
  readonly keywords?: readonly MessageDescriptor[];
  readonly shortcut?: string;
  readonly permission?: Permission;
  readonly input?: z.ZodType<Input>;
  readonly row?: boolean;
  /**
   * Autossuficiente para página personalizada (08 §4.2): todo o input vem do schema, nada do estado
   * da tela. Exige `input`.
   */
  readonly page?: boolean;
  /** `false` tira a ação de todo agente (WebMCP e Luy), mesmo com a tela aberta (U24, U56). */
  readonly agent?: false;
  /** A linha do Luy no chat quando ele roda a ação: "Buscando produto" e "Produto encontrado". Sem ela, o `label`. */
  readonly activity?: ActionActivity;
  /** O que o `run` devolve vai ao agente como resultado estruturado (U56); `undefined` vira "ok". */
  readonly run: (context: ActionContext<Input>) => unknown;
}

export interface ReadAction<Input = Record<string, never>> extends ActionBase<Input> {
  readonly kind: 'read' | 'navigate' | 'draft';
  readonly preview?: never;
  readonly stepUp?: never;
}

export interface EffectAction<Input = Record<string, never>> extends ActionBase<Input> {
  readonly kind: 'write' | 'destructive';
  readonly preview: (input: Input) => ActionPreview | Promise<ActionPreview>;
  /**
   * Pede senha + código MFA (F2a, `POST /Auth/StartAuth`). O `run` **só prepara**: abre o diálogo
   * de step-up na tela, e a pessoa confirma. O agente recebe uma ferramenta que só prepara, nunca
   * executa (U24); a paleta e o executor da tela não mostram "Feito". Não combina com `page: true`.
   * Com `{ action }` (step-up novo), o executor da tela abre o painel com a prévia e roda o `run` com a
   * autorização; o agente só recebe a resposta de que nada foi executado.
   */
  readonly stepUp?: true | ScreenStepUpGrant<Input>;
}

export type ScreenAction<Input = never> = ReadAction<Input> | EffectAction<Input>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- a lista mistura ações com inputs diferentes
export type AnyScreenAction = ScreenAction<any>;

export interface ScreenNav {
  readonly group: MessageDescriptor;
  readonly icon: string;
  readonly order: number;
}

/**
 * Onde a tela mora na hierarquia (U42). Toda tela declara: é daqui que sai o breadcrumb, o voltar
 * do iPhone e a trilha que o agente recebe. Trilha: seção do menu › telas-mãe › tela › aba.
 */
export interface ScreenPlacement {
  /** Ícone Lucide (kebab-case) desta tela no breadcrumb e no ⌘K. */
  readonly icon: string;
  /** Seção do menu (id do grupo da navegação, ex.: `register`). Só na raiz da trilha. */
  readonly section?: string;
  /** Tela-mãe (id): o detalhe aponta para a lista, a sub-tela para o detalhe. */
  readonly parent?: string;
  /** Sub-telas por aba (`?tab=`), na ordem em que aparecem. */
  readonly tabs?: readonly {
    readonly id: string;
    readonly label: MessageDescriptor;
    readonly icon?: string;
  }[];
  /** Tela de um registro: o nome vem de `useScreenCrumb`; enquanto carrega, mostra este rótulo. */
  readonly record?: MessageDescriptor;
  /** Tela em etapas (Fluxo guiado, U53): enquanto carrega, a casca mostra o esqueleto `flow`. */
  readonly flow?: boolean;
}

export interface ScreenContext {
  readonly summary: string;
  readonly entity?: { readonly type: string; readonly id: string };
  readonly fields?: Readonly<Record<string, unknown>>;
  readonly sensitivity: 'public' | 'internal' | 'restricted';
}

export interface ScreenManifest {
  readonly access?: {
    /** Além de `permission`: basta uma destas (cada uma com o próprio `strict`). */
    readonly anyOf?: readonly Permission[];
    readonly rootTenancy?: boolean;
    readonly flag?: { readonly key: string } & (
      | { readonly equals: string | number | boolean | null; readonly notEquals?: never }
      | { readonly notEquals: string | number | boolean | null; readonly equals?: never }
    );
  };
  readonly id: string;
  readonly path: string;
  readonly title: MessageDescriptor;
  readonly description: AgentText;
  readonly permission?: Permission;
  readonly nav?: ScreenNav;
  readonly place: ScreenPlacement;
  readonly agent?: boolean;
  readonly actions: readonly AnyScreenAction[];
}

export const EFFECT_KINDS: readonly ActionKind[] = ['write', 'destructive'];

export function isEffect(action: AnyScreenAction): action is EffectAction<unknown> {
  return EFFECT_KINDS.includes(action.kind);
}

/** Ação com step-up (senha + MFA): `run` só prepara, e o agente nunca executa (U24). */
export function isStepUp(action: AnyScreenAction): boolean {
  return isEffect(action) && action.stepUp !== undefined;
}

/** O step-up novo da ação (`stepUp: { action }`); `undefined` sem ele ou com o de `StartAuth` (`stepUp: true`). */
export function stepUpGrantOf(action: AnyScreenAction): ScreenStepUpGrant<unknown> | undefined {
  return isEffect(action) && typeof action.stepUp === 'object' ? action.stepUp : undefined;
}
